import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMountedState } from 'react-use'
import { createContainer } from 'unstated-next'

import {
  CartLineInput,
  CartLineUpdateInput,
  CustomAttribute,
  GetCartVariantsQuery,
} from '../../../__generated__/graphql/catalog/graphql'
import {
  reportClientError,
  reportClientEvent,
} from '../../../app/_components/chrome/scripts/DataDogRumScript'
import { cookieKeys } from '../../../app/_config/Cookies.config'
import { useCookies } from '../../../app/_providers/CookiesProvider.client'
import { useGraphqlClients } from '../../../app/_providers/GraphqlClientsProvider.client'
import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import {
  type SignalTrackingLocation,
  useTrackCartItemAdded,
  useTrackCartItemRemoved,
  useTrackCartItemUpdated,
  useTrackCartModified,
  useTrackCartOpened,
  useTrackProceedToCheckout,
  useTrackProceedToReturnsPortal,
} from '../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { isDefinedAndNotNull } from '../../../app/_utils/isDefinedAndNotNull'
import { MappedBundle } from '../../../components/pages/cart/utils'
import { useCartStore } from '../../../lib/adapters/cart-persistence/useCartStore'
import { buildLoopCheckoutUrl } from '../../../lib/adapters/loop/loopCart'
import {
  areLoopParamsValid,
  clearLoopParams,
  getLoopParams,
  getLoopSession,
  setLoopSession,
} from '../../../lib/adapters/loop/loopStorage'
import { mapPropertiesToCustomAttributes } from '../../../lib/cart-client/utils'
import { FETCH_CART_VARIANTS } from '../../../lib/graphql/queries/FETCH_CART_VARIANTS'
import { QueryStatus } from '../../../lib/hooks/types'
import { GtmVariant } from '../../../lib/hooks/useGoogleTagManager'
import { deriveMapFromList, isEmbroiderableProduct } from '../../../lib/utils'
import { Cart, CartItem } from '../../../types/figs'

import {
  areCartLinesEqual,
  augmentPersistedCartItemsWithFigsData,
  blankCartItem,
  getGuidGroup,
  mapCartLinesToBundlePrograms,
  mergeCollectionAttribution,
  planCartRemoval,
  useConvertCartItemIntoSignalData,
} from './utils'

export interface UseCartNewCartItem {
  finalSale: boolean
  item: GtmVariant
  properties?: Record<string, string | undefined>
  quantity?: number
  // Where the item was added from, sent with the cart signals only.
  tracking?: SignalTrackingLocation
}

export interface UseCartOutput {
  cart: Omit<Cart, 'id'>
  /** Placeholders for lines whose add request is still in flight, minus any already in the cart. */
  pendingLines: ReadonlyArray<CartItem>
  addItems(itemsToAdd: UseCartNewCartItem[]): void
  addGlobalAttributes(attributes: CustomAttribute[]): void
  removeItems(keys: string[], options?: { removeAssociatedPortalProduct?: boolean }): void
  updateItemsQuantity(
    keys: string[],
    calculateQuantity: ((currentQuantity: number) => number) | number
  ): void
  /**
   * Cart embroidery upsell: attach embroidery to an item already in the cart by
   * adding the embroidered lines (built by the caller via
   * `makeEmbroideredCartItem`) and then removing the original bare line.
   */
  addEmbroideryToExistingCartItem(
    embroideredItems: UseCartNewCartItem[],
    existingItemKey: string
  ): Promise<void>
  proceedToCheckout(props?: { discountCode?: string }): Promise<void>
  /**
   * Records that the cart was opened. The `cart_opened` signal is sent once the cart has
   * finished loading, so an open right after an add to bag includes the added item.
   */
  trackCartOpened(): void
  /**
   * Whether a removal of this cart-line key is currently in flight. Lets callers
   * (e.g. the embroidery upsell entry point) avoid acting on a line that is mid-delete.
   */
  isItemRemovalInProgress(key: string): boolean
  status: QueryStatus
  lastAddedItems: Array<{
    sku: string
    properties: Record<string, string | undefined>
  }>
  errorMessage: string | undefined
  bundlePrograms: Record<string, MappedBundle>
}

// Matches on "Gift", "Gift Note", "Gift Box", but not "Gift Card"
const GIFT_REGEX = /Gift(?! Card)/

export const EMBROIDERY_UPSELL_ATTRIBUTE_KEY = '_embroidery_upsell'

// Longest we show placeholders while lines' product data loads
const CART_LINE_HOLD_TIMEOUT_MS = 10_000

type PendingAdd = {
  id: number
  // Placeholders built from the add input while the request is in flight; emptied once it settles
  lines: CartItem[]
  categoryBySku: Record<string, string>
  // Cart item count when the add started, shown until the add has loaded
  itemCount: number
  inFlight: boolean
}

const getAddedItemCategory = (item: GtmVariant): string =>
  'variantGroup' in item ? item.variantGroup.primaryCategory.id : item.category

function useCart(): UseCartOutput {
  const [cookies] = useCookies([cookieKeys.discountCode.key, cookieKeys.facebook.key])
  const discountCookieVal = cookies[cookieKeys.discountCode.key]
  const facebookCookieVal = cookies[cookieKeys.facebook.key]
  const checkIsMounted = useMountedState()
  const { trackProceedToCheckout } = useTrackProceedToCheckout()
  const { trackCartOpened: sendCartOpenedSignal } = useTrackCartOpened()
  const { trackProceedToReturnsPortal } = useTrackProceedToReturnsPortal()
  const { trackCartItemAdded } = useTrackCartItemAdded()
  const { trackCartItemRemoved } = useTrackCartItemRemoved()
  const { trackCartItemUpdated } = useTrackCartItemUpdated()
  const convertCartItemIntoSignalData = useConvertCartItemIntoSignalData()
  const { trackCartModified } = useTrackCartModified()
  const { locale, currency } = useLocalization()
  const {
    cart: { finalSale, errorMessages },
  } = useTranslation()
  const { cacheOptimizedClient } = useGraphqlClients()
  const [lastAddedItems, setLastAddedItems] = useState<UseCartOutput['lastAddedItems']>([])
  const [errorMessage, setErrorMessage] = useState<string>()
  // When a user has added embroidery to the cart via embroidery upsell (add-then-remove),
  // this holds the displayed cart at this snapshot, with the subtotal in its loading state,
  // so the customer never sees a flicker
  const [frozenCartSnapshot, setFrozenCartSnapshot] = useState<Omit<Cart, 'id'> | null>(null)
  // SKUs whose lines must load before the snapshot is released; null while the conversion runs
  const [snapshotAwaitedSkus, setSnapshotAwaitedSkus] = useState<ReadonlySet<string> | null>(null)
  // Adds whose lines are still in flight or loading, oldest first
  const [pendingAdds, setPendingAdds] = useState<ReadonlyArray<PendingAdd>>([])
  const nextPendingAddId = useRef(0)
  const [isHoldTimedOut, setIsHoldTimedOut] = useState(false)
  const holdTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const isHoldingRef = useRef(false)
  // In-flight removals tracked twice: the ref is read synchronously to dedupe
  // duplicate delete clicks; the state is a render-visible mirror exposed via
  // `isItemRemovalInProgress` (e.g. to disable the upsell button on a line being removed).
  const removeKeysRef = useRef<Set<string>>(new Set())
  const [pendingRemovalKeys, setPendingRemovalKeys] = useState<ReadonlySet<string>>(() => new Set())

  const {
    status: cartStoreStatus,
    cart: cartStoreCart,
    addItemsToCart: addItemsToCartWithoutEvent,
    removeItemsFromCart: removeItemsFromCartWithoutEvent,
    updateItemsInCart: updateItemsInCartWithoutEvent,
    addCartAttributes,
    retrieveCheckoutUrl,
    retrieveLoopCartToken,
  } = useCartStore()

  const mappedBundlePrograms = useMemo(() => {
    return mapCartLinesToBundlePrograms(cartStoreCart.lines)
  }, [cartStoreCart.lines])

  const uniqueCartSkus = useMemo(() => {
    // `sku` is typed non-null via `!` in useCartStore, but Shopify can return lines without one
    return [...new Set(cartStoreCart.lines.map(i => i.sku).filter(isDefinedAndNotNull))]
  }, [cartStoreCart.lines])

  // NOTE: It doesn't make sense to "augment" data at the level of the cart provider...
  // we don't know yet what data people need. At most we should just be exposing ids for
  // cart items and letting different UIs decide what they want to query based on what they
  // need to display. They can also do pagination then... whereas here we end up calling 100s
  // of queries if we have 100s of cart items. Even if a user never once even looks inside the cart
  // at those items. It is wasteful and creates coupling of data types.
  const [variantsStatus, setVariantsStatus] = useState<UseCartOutput['status']>('pending')
  const [variants, setVariants] = useState<
    NonNullable<GetCartVariantsQuery['productCatalogVariants']>
  >([])

  // (Re)started on each add and variant fetch; on expiry, lines render as-is and it's reported
  const restartHoldTimer = useCallback(() => {
    clearTimeout(holdTimerRef.current)
    setIsHoldTimedOut(false)
    holdTimerRef.current = setTimeout(() => {
      if (!isHoldingRef.current) return
      setIsHoldTimedOut(true)
      setPendingAdds([])
      reportClientError({
        error: new Error('Cart line data did not load before the placeholder timeout.'),
        context: { scope: 'useCart', label: 'cartLineHoldTimeout' },
      })
    }, CART_LINE_HOLD_TIMEOUT_MS)
  }, [])

  useEffect(() => () => clearTimeout(holdTimerRef.current), [])

  // Each variant fetch (a change in cart SKUs) restarts the hold timer
  useEffect(() => {
    restartHoldTimer()
  }, [uniqueCartSkus, restartHoldTimer])

  // TODO: we don't really want to do this here. Each respective UI should be loading this data.
  // TODO: update types so we aren't returning the desired fields, then add the query as needed.
  const previousFetchedVariantSkus = useRef<string[]>([])
  // The state updates below only apply on the re-render, so this pass reads the flag instead
  let hasStartedVariantFetch = false
  if (previousFetchedVariantSkus.current !== uniqueCartSkus && checkIsMounted()) {
    previousFetchedVariantSkus.current = uniqueCartSkus
    hasStartedVariantFetch = true
    setVariantsStatus('pending')
    setIsHoldTimedOut(false)
    const newVariants: typeof variants = []
    const loadVariantDataForSkus = async () => {
      let variantFailed = false
      const variantQueries = uniqueCartSkus.map(sku => {
        return cacheOptimizedClient.query({
          query: FETCH_CART_VARIANTS,
          variables: { skus: [sku] },
          context: {
            includeUnavailable: true, // We should fetch data for anything the cart API tells us is in the cart. So turning off filtering.
          },
        })
      })
      const variantResponses = await Promise.allSettled(variantQueries)

      for (const variantResponse of variantResponses) {
        if (variantResponse.status === 'fulfilled') {
          newVariants.push(...(variantResponse.value.data?.productCatalogVariants ?? []))
        } else {
          variantFailed = true
        }
      }

      setVariants(newVariants)
      if (variantFailed) {
        setVariantsStatus('rejected')
      } else {
        setVariantsStatus('resolved')
      }
    }

    loadVariantDataForSkus()
  }

  const isAddInFlight = pendingAdds.some(add => add.inFlight)
  // Lines that haven't loaded are placeholders while their data is still on its way
  const shouldHoldUnloadedLines =
    (variantsStatus === 'pending' || hasStartedVariantFetch || isAddInFlight) &&
    (!isHoldTimedOut || hasStartedVariantFetch)
  const pendingCategoryBySku = useMemo(
    () =>
      pendingAdds.reduce<Record<string, string>>(
        (acc, add) => ({ ...acc, ...add.categoryBySku }),
        {}
      ),
    [pendingAdds]
  )

  const cart = useMemo<Omit<Cart, 'id'>>(() => {
    const variantsBySku = deriveMapFromList(variants, o => o.sku)

    const augmentedCartItems = augmentPersistedCartItemsWithFigsData(
      cartStoreCart.lines,
      variantsBySku,
      locale,
      mappedBundlePrograms,
      shouldHoldUnloadedLines ? { categoryBySku: pendingCategoryBySku } : undefined
    )

    // TODO: eventually use localized return values from cart-service
    const totalPrice: number = augmentedCartItems.reduce(
      (sum, item) => sum + item.effectivePrice * item.quantity,
      0
    )

    return {
      items: deriveMapFromList(augmentedCartItems, o => o.key),
      itemCount: cartStoreCart.totalQuantity,
      totalPrice,
      attributes: cartStoreCart.attributes,
    }
  }, [
    variants,
    cartStoreCart.lines,
    cartStoreCart.totalQuantity,
    cartStoreCart.attributes,
    locale,
    mappedBundlePrograms,
    shouldHoldUnloadedLines,
    pendingCategoryBySku,
  ])

  const placeholderSkus = useMemo(
    () =>
      new Set(
        Object.values(cart.items)
          .filter(item => item.isPlaceholder)
          .map(item => item.sku)
      ),
    [cart.items]
  )

  // Release settled adds and the upsell snapshot once their lines are no longer placeholders.
  // Adjusted during render so the release lands in the same commit as the loaded lines.
  const loadedAddIds = pendingAdds
    .filter(
      add => !add.inFlight && !Object.keys(add.categoryBySku).some(sku => placeholderSkus.has(sku))
    )
    .map(add => add.id)
  if (loadedAddIds.length > 0) {
    setPendingAdds(prev => prev.filter(add => !loadedAddIds.includes(add.id)))
  }
  if (
    frozenCartSnapshot &&
    snapshotAwaitedSkus &&
    ![...snapshotAwaitedSkus].some(sku => placeholderSkus.has(sku))
  ) {
    setFrozenCartSnapshot(null)
    setSnapshotAwaitedSkus(null)
  }

  // The upsell snapshot alone doesn't count: it's released when the conversion settles
  const isHolding = pendingAdds.length > 0 || placeholderSkus.size > 0
  useEffect(() => {
    isHoldingRef.current = isHolding
  }, [isHolding])

  const pendingLines = useMemo<ReadonlyArray<CartItem>>(() => {
    const liveLines = Object.values(cart.items)
    // Newest add first, matching Shopify's order for new lines
    return [...pendingAdds]
      .reverse()
      .flatMap(add => add.lines)
      .filter(
        pendingLine =>
          !liveLines.some(liveLine =>
            areCartLinesEqual(
              {
                attributes: mapPropertiesToCustomAttributes(pendingLine.properties),
                sku: pendingLine.sku,
              },
              {
                attributes: mapPropertiesToCustomAttributes(liveLine.properties),
                sku: liveLine.sku,
              }
            )
          )
      )
  }, [cart.items, pendingAdds])

  const displayedCart = useMemo<Omit<Cart, 'id'>>(() => {
    // While an embroidery conversion is in flight, expose the frozen snapshot so the
    // UI holds the pre-conversion state until the new lines have loaded.
    if (frozenCartSnapshot) return frozenCartSnapshot
    const heldItemCount = pendingAdds[0]?.itemCount
    return heldItemCount === undefined ? cart : { ...cart, itemCount: heldItemCount }
  }, [cart, frozenCartSnapshot, pendingAdds])

  /**
   * Low-level removal: removes exactly the lines passed and fires the
   * `trackCartItemRemoved` analytics event. Rejects if the store call fails.
   *
   * Use this when you need to know whether a removal actually succeeded — it
   * surfaces store failures to the caller (unlike `removeItems`, which swallows
   * them). It does NOT run any of the cart-aware cascade logic in `removeItems`
   * (related embroidery lines, logo-digitization cleanup, embroidery re-add / kit
   * recalculation, gift-item sweep), so only reach for it when removing specific,
   * self-contained lines where that cascade either doesn't apply or is unwanted.
   * For user-driven cart-row deletes, prefer `removeItems`.
   */
  const removeItemsFromCartStore = useCallback(
    async (itemsToRemove: CartItem[]) => {
      await removeItemsFromCartWithoutEvent(itemsToRemove.map(itemToRemove => itemToRemove.key))
      itemsToRemove.forEach(itemToRemove => {
        trackCartItemRemoved({
          cartId: cartStoreCart.id,
          item: convertCartItemIntoSignalData(itemToRemove),
        })
      })
    },
    [
      cartStoreCart.id,
      convertCartItemIntoSignalData,
      removeItemsFromCartWithoutEvent,
      trackCartItemRemoved,
    ]
  )

  const addGlobalAttributesToCartStore = useCallback(
    async (attributes: CustomAttribute[]) => {
      await addCartAttributes(attributes)
    },
    [addCartAttributes]
  )

  const addItemsToCartStore = useCallback(
    async (incomingItems: UseCartNewCartItem[], options?: { skipPendingLines?: boolean }) => {
      /**
       * Shopify combines variants into one line item with the quantity being
       * the sum of the individual variant quantities if:
       * 1. The variant IDs match
       * 2. The custom line item properties match
       *
       * We have one attribute, `_collectionAttribution`, that should be updated
       * instead of being treated as unique. To manage that this behavior, we must
       * identify a matching line item and perform an update instead of an add
       * (if applicable).
       */
      const itemsToAdd: Array<{
        item: CartLineInput
        properties: CartItem['properties']
        __source: UseCartNewCartItem
        // every incoming item merged into this add, tracked individually
        trackedSources: Array<{ source: UseCartNewCartItem; quantity: number }>
      }> = []
      const updatesByLineKey = new Map<
        string,
        {
          source: CartItem
          properties: CartItem['properties']
          quantity: number
          tracking: SignalTrackingLocation | undefined
        }
      >()
      incomingItems.forEach(incomingItem => {
        // apply defaults up front so matching and the add payload see the same attributes
        const properties = { ...incomingItem.properties }
        if (properties._kitId && !properties._setUid) {
          properties._setUid = properties._kitId + '1'
        }
        if (incomingItem.finalSale && !properties._LPROP) {
          properties._LPROP = 'final sale'
        }
        const attributes = mapPropertiesToCustomAttributes(properties)
        const incomingQuantity = incomingItem.quantity ?? 1

        const existingMatchingLineItem = Object.values(cart.items).find(e =>
          areCartLinesEqual(
            { attributes, sku: incomingItem.item.sku },
            { attributes: mapPropertiesToCustomAttributes(e.properties), sku: e.sku }
          )
        )
        if (existingMatchingLineItem) {
          // accumulate per line so several matches in one call become a single update
          const pending = updatesByLineKey.get(existingMatchingLineItem.key) ?? {
            source: existingMatchingLineItem,
            properties: existingMatchingLineItem.properties,
            quantity: existingMatchingLineItem.quantity,
            tracking: incomingItem.tracking,
          }
          pending.quantity += incomingQuantity
          if (properties._collectionAttribution) {
            pending.properties = {
              ...pending.properties,
              _collectionAttribution: mergeCollectionAttribution(
                pending.properties._collectionAttribution,
                properties._collectionAttribution
              ),
            }
          }
          updatesByLineKey.set(existingMatchingLineItem.key, pending)
          return
        }

        // merge identical incoming items so each add maps to exactly one Shopify line
        const identicalAdd = itemsToAdd.find(e =>
          areCartLinesEqual(
            { attributes, sku: incomingItem.item.sku },
            { attributes: mapPropertiesToCustomAttributes(e.properties), sku: e.__source.item.sku }
          )
        )
        if (identicalAdd) {
          identicalAdd.item.quantity += incomingQuantity
          identicalAdd.trackedSources.push({ source: incomingItem, quantity: incomingQuantity })
          if (properties._collectionAttribution) {
            identicalAdd.properties = {
              ...identicalAdd.properties,
              _collectionAttribution: mergeCollectionAttribution(
                identicalAdd.properties._collectionAttribution,
                properties._collectionAttribution
              ),
            }
            identicalAdd.item.attributes = mapPropertiesToCustomAttributes(identicalAdd.properties)
          }
          return
        }

        itemsToAdd.push({
          item: {
            merchandiseId: incomingItem.item.shopifyId,
            quantity: incomingQuantity,
            attributes: attributes.length > 0 ? attributes : undefined,
          },
          properties,
          __source: incomingItem,
          trackedSources: [{ source: incomingItem, quantity: incomingQuantity }],
        })
      })
      const itemsToUpdate = Array.from(updatesByLineKey.entries()).map(([id, pending]) => ({
        item: {
          id,
          attributes: mapPropertiesToCustomAttributes(pending.properties),
          quantity: pending.quantity,
        } satisfies CartLineUpdateInput,
        __source: pending.source,
        tracking: pending.tracking,
      }))

      if (itemsToAdd.length > 0) {
        const addId = nextPendingAddId.current
        nextPendingAddId.current += 1
        const pendingAdd: PendingAdd = {
          id: addId,
          lines: options?.skipPendingLines
            ? []
            : itemsToAdd.map(({ item, properties, __source }, index) => ({
                ...blankCartItem,
                key: `pending-line-${addId}-${index}`,
                sku: __source.item.sku,
                quantity: item.quantity,
                properties,
                productType: getAddedItemCategory(__source.item),
                isPlaceholder: true,
              })),
          categoryBySku: Object.fromEntries(
            itemsToAdd.map(({ __source }) => [
              __source.item.sku,
              getAddedItemCategory(__source.item),
            ])
          ),
          itemCount: cart.itemCount,
          inFlight: true,
        }
        setPendingAdds(prev => [...prev, pendingAdd])
        restartHoldTimer()

        try {
          await addItemsToCartWithoutEvent(itemsToAdd.map(itemToAdd => itemToAdd.item))
        } catch (error) {
          setPendingAdds(prev => prev.filter(add => add.id !== addId))
          throw error
        }
        // The live lines take over from the pending ones
        setPendingAdds(prev =>
          prev.map(add => (add.id === addId ? { ...add, lines: [], inFlight: false } : add))
        )
        itemsToAdd.forEach(itemToAdd => {
          itemToAdd.trackedSources.forEach(({ source, quantity }) => {
            trackCartItemAdded({
              cartId: cartStoreCart.id,
              item: convertCartItemIntoSignalData({ ...source, quantity }),
              tracking: source.tracking,
            })
          })
        })
      }
      if (itemsToUpdate.length > 0) {
        await updateItemsInCartWithoutEvent(itemsToUpdate.map(itemToUpdate => itemToUpdate.item))
        itemsToUpdate.forEach(itemsToUpdate => {
          trackCartItemUpdated({
            cartId: cartStoreCart.id,
            item: {
              ...convertCartItemIntoSignalData({
                ...itemsToUpdate.__source,
                quantity: itemsToUpdate.item.quantity,
              }),
              previousQuantity: itemsToUpdate.__source.quantity,
            },
            tracking: itemsToUpdate.tracking,
          })
        })
      }
    },
    [
      addItemsToCartWithoutEvent,
      cart.items,
      cart.itemCount,
      cartStoreCart.id,
      convertCartItemIntoSignalData,
      restartHoldTimer,
      trackCartItemAdded,
      trackCartItemUpdated,
      updateItemsInCartWithoutEvent,
    ]
  )

  const setItemQuantitiesInCartStore = useCallback(
    async (itemsToUpdate: Array<{ item: CartItem; newQuantity: number }>) => {
      itemsToUpdate.forEach(({ item, newQuantity }) => {
        const oldQuantity = item.quantity
        trackCartItemUpdated({
          cartId: cartStoreCart.id,
          item: {
            ...convertCartItemIntoSignalData(item),
            quantity: newQuantity,
            previousQuantity: oldQuantity,
          },
        })
      })

      const convertedItemsToUpdate: CartLineUpdateInput[] = itemsToUpdate.map(
        ({ item, newQuantity }) => ({
          id: item.cartStoreKey ?? item.key,
          quantity: newQuantity,
          attributes: mapPropertiesToCustomAttributes(item.properties),
        })
      )

      await updateItemsInCartWithoutEvent(convertedItemsToUpdate)
    },
    [
      cartStoreCart.id,
      convertCartItemIntoSignalData,
      trackCartItemUpdated,
      updateItemsInCartWithoutEvent,
    ]
  )

  const status = useMemo<UseCartOutput['status']>(() => {
    // Keep the cart in its loading state for the full duration of an embroidery
    // upsell conversion so the subtotal stays until it finishes loading the new state
    if (frozenCartSnapshot) return 'pending'
    if (cartStoreStatus === 'pending' || variantsStatus === 'pending') return 'pending'
    // An add is in flight or its lines are still placeholders
    if (pendingAdds.length > 0) return 'pending'
    if (variantsStatus === 'rejected' || cartStoreStatus === 'rejected') return 'rejected'
    return 'resolved'
  }, [cartStoreStatus, variantsStatus, frozenCartSnapshot, pendingAdds.length])

  useEffect(() => {
    // if we are still loading data or we haven't obtained a cart yet, skip signaling
    if (status !== 'resolved' || cartStoreCart.id === null) return
    const signalCartItems = Object.values(cart.items)
      .map(cartItem => {
        return {
          ...convertCartItemIntoSignalData(cartItem),
          title: cartItem.title,
        }
      })
      .filter(isDefinedAndNotNull)
    // Note that these Iterable properties are used by external teams at FIGS. If there are changes that alter the data getting passed through,
    // please communicate it with relevant stakeholders.
    trackCartModified({
      id: cartStoreCart.id,
      items: signalCartItems,
    })
  }, [cartStoreCart.id, cart.items, trackCartModified, status, convertCartItemIntoSignalData])

  const [isCartOpenedSignalPending, setIsCartOpenedSignalPending] = useState(false)
  const trackCartOpened = useCallback(() => setIsCartOpenedSignalPending(true), [])
  // An effect, not the open handler, because an add to bag opens the cart before the add
  // resolves; waiting for `status` keeps the added item in the signal.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!isCartOpenedSignalPending || status !== 'resolved') return
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
    setIsCartOpenedSignalPending(false)
    sendCartOpenedSignal({ cartId: cartStoreCart.id, cart })
  }, [cart, cartStoreCart.id, isCartOpenedSignalPending, sendCartOpenedSignal, status])

  const addGlobalAttributes = useCallback<UseCartOutput['addGlobalAttributes']>(
    async attributes => {
      await addGlobalAttributesToCartStore(attributes)
    },
    [addGlobalAttributesToCartStore]
  )

  const addItems = useCallback<UseCartOutput['addItems']>(
    async (itemsToAdd): Promise<void> => {
      setErrorMessage(undefined)
      try {
        await addItemsToCartStore(itemsToAdd)
        // TODO: depending on business needs for `lastAddedItems`,
        // consider filtering our embroidery items here.
        setLastAddedItems(
          itemsToAdd.map(e => ({ sku: e.item.sku, properties: { ...e.properties } }))
        )
      } catch {
        // if error, reset `lastAddedSkus` to prevent past success values from bleeding through
        setLastAddedItems([])
        setErrorMessage(errorMessages.addLine)
      }
    },
    [addItemsToCartStore, errorMessages.addLine]
  )

  /**
   * Public, cart-aware removal — the default for user-driven cart-row deletes.
   *
   * Removes the planned cascade (`planCartRemoval`), re-adds stripped garments, and sweeps lone
   * gift items. Keys already being removed are skipped. It SWALLOWS store failures — on error it
   * sets `errorMessages.removeLine` and resolves normally. To branch on success/failure,
   * call `removeItemsFromCartStore` directly instead.
   */
  const removeItems = useCallback<UseCartOutput['removeItems']>(
    async (keys, options): Promise<void> => {
      const pendingRemovals = removeKeysRef.current
      const { itemsToRemove: plannedRemovals, itemsToAddBack } = planCartRemoval({
        cartItems: cart.items,
        keys,
        pendingKeys: pendingRemovals,
        removeAssociatedPortalProduct: options?.removeAssociatedPortalProduct,
        finalSaleKey: finalSale,
      })
      if (plannedRemovals.length === 0 && itemsToAddBack.length === 0) return

      setErrorMessage(undefined)
      const itemsToRemove = new Map(plannedRemovals.map(item => [item.key, item]))

      const removalKeys = [...itemsToRemove.keys()]
      removalKeys.forEach(key => pendingRemovals.add(key))
      setPendingRemovalKeys(new Set(pendingRemovals))

      try {
        if (itemsToRemove.size > 0) {
          // If the remaining cart items are just Gift Boxes or Gift Notes, they should be removed.
          const remainingCartItems = Object.values(cart.items).filter(
            ({ key }) => ![...itemsToRemove.keys()].includes(key)
          )

          // Using a regex as the `categoryId` values aren't consistent between staging & prod.
          if (remainingCartItems.every(item => GIFT_REGEX.test(item.categoryId))) {
            remainingCartItems.forEach(item => {
              itemsToRemove.set(item.key, item)
            })
          }

          try {
            await removeItemsFromCartStore([...itemsToRemove.values()])
          } catch {
            setErrorMessage(errorMessages.removeLine)
          }
        }
        if (itemsToAddBack.length > 0) {
          try {
            await addItemsToCartWithoutEvent(itemsToAddBack)
          } catch {
            // if we have errors here, we should just ignore them; consider it an imperfect removal.
          }
        }
      } finally {
        removalKeys.forEach(key => pendingRemovals.delete(key))
        setPendingRemovalKeys(new Set(pendingRemovals))
      }
    },
    [
      addItemsToCartWithoutEvent,
      removeItemsFromCartStore,
      errorMessages.removeLine,
      cart.items,
      finalSale,
    ]
  ) // end removeItems

  const updateItemsQuantity = useCallback<UseCartOutput['updateItemsQuantity']>(
    async (keys, calculateQuantity) => {
      const itemsToUpdate: Map<string, { item: CartItem; newQuantity: number }> = new Map<
        string,
        { item: CartItem; newQuantity: number }
      >()
      const itemsToRemove: string[] = []

      keys.forEach(key => {
        const itemToUpdate = cart.items[key]
        if (!itemToUpdate) return

        const newQuantity =
          typeof calculateQuantity === 'function'
            ? calculateQuantity(itemToUpdate.quantity)
            : itemToUpdate.quantity + calculateQuantity

        if (newQuantity <= 0) {
          itemsToRemove.push(key)
          return
        }

        if (isEmbroiderableProduct(itemToUpdate)) {
          getGuidGroup(itemToUpdate.properties._guid, cart.items).forEach(item => {
            itemsToUpdate.set(item.key, { item, newQuantity })
          })
        } else {
          itemsToUpdate.set(itemToUpdate.key, { item: itemToUpdate, newQuantity })
        }
      })

      if (itemsToRemove.length > 0) {
        removeItems(itemsToRemove)
      }

      if (itemsToUpdate.size > 0) {
        await setItemQuantitiesInCartStore([...itemsToUpdate.values()])
      }
    },
    [cart.items, removeItems, setItemQuantitiesInCartStore]
  )

  /**
   * Cart embroidery upsell: attach embroidery to an item already in the cart.
   *
   * Shopify keys line identity on SKU + properties, so the bare garment can't be
   * mutated in place. We add the embroidered lines first so a failed add never loses
   * the item, then remove the original. Only once BOTH the add and the remove fully
   * succeed do we stamp the order-level upsell attribution, so neither a failed add
   * (item kept, not stamped) nor a failed remove (duplicate garment, not stamped)
   * marks the order as a clean upsell conversion.
   */
  const addEmbroideryToExistingCartItem = useCallback<
    UseCartOutput['addEmbroideryToExistingCartItem']
  >(
    async (embroideredItems, existingItemKey) => {
      setErrorMessage(undefined)
      setFrozenCartSnapshot(displayedCart)
      setSnapshotAwaitedSkus(null)
      let awaitedSkus: ReadonlySet<string> | null = null
      try {
        try {
          // The snapshot covers the wait, so no pending lines
          await addItemsToCartStore(embroideredItems, { skipPendingLines: true })
          setLastAddedItems(
            embroideredItems.map(e => ({ sku: e.item.sku, properties: { ...e.properties } }))
          )
        } catch {
          setLastAddedItems([])
          setErrorMessage(errorMessages.addLine)
          // Bail before removing so we never drop the customer's item on a failed add.
          return
        }

        const existingItem = cart.items[existingItemKey]
        try {
          await removeItemsFromCartStore(existingItem ? [existingItem] : [])
        } catch {
          setErrorMessage(errorMessages.removeLine)
          return
        }
        awaitedSkus = new Set(embroideredItems.map(e => e.item.sku))
        // Conversion succeeded: attribute the order as a cart embroidery upsell.
        await addGlobalAttributesToCartStore([
          { key: EMBROIDERY_UPSELL_ATTRIBUTE_KEY, value: 'true' },
        ])
      } finally {
        // A failure releases now; a success holds until the new lines have loaded
        if (awaitedSkus) {
          setSnapshotAwaitedSkus(awaitedSkus)
        } else {
          setFrozenCartSnapshot(null)
        }
      }
    },
    [
      addGlobalAttributesToCartStore,
      addItemsToCartStore,
      errorMessages.addLine,
      errorMessages.removeLine,
      removeItemsFromCartStore,
      cart.items,
      displayedCart,
    ]
  )

  const proceedToCheckout = useCallback<UseCartOutput['proceedToCheckout']>(
    async options => {
      setErrorMessage(undefined)
      const providedDiscountCode = options?.discountCode

      if (areLoopParamsValid(getLoopParams())) {
        const session = getLoopSession()
        if (session && session.loop_return_id) {
          try {
            const token = await retrieveLoopCartToken()
            if (token) {
              // set token on Loop session
              setLoopSession({ ...session, token })

              // build redirectUri
              const redirectUri = buildLoopCheckoutUrl()

              // clear other Loop params since the user may not return for this Loop flow
              clearLoopParams()

              reportClientEvent({
                label: 'navigated-to-returns-portal',
                context: {
                  scope: 'useCart',
                  itemCount: `${cart.itemCount}`,
                  cartValue: `${cart.totalPrice}`,
                },
              })

              const signalCheckoutItems = Object.values(cart.items)
                .map(convertCartItemIntoSignalData)
                .filter(isDefinedAndNotNull)

              trackProceedToReturnsPortal({
                loopCartToken: token,
                loopCheckoutURL: redirectUri,
                items: signalCheckoutItems,
              })

              // Navigate user to Loop checkout, intentionally leaving the cart intact.
              // `app-figs-cart-service` will read the cart as the sync source before creating
              // a new Loop cart for handoff with Loop. Clearing the cart here would empty
              // it before the sync and handoff occurs, removing our ability to correctly map
              // cart and line item attributes.
              window.location.assign(redirectUri)

              // return to prevent standard checkout logic from running
              return
            } else {
              throw new Error('Failed to retrieve Loop cart token')
            }
          } catch (error) {
            setErrorMessage(errorMessages.proceedToCheckout)
            reportClientError({
              error: new Error('Error occurred creating/proceeding to Loop checkout.'),
              context: { scope: 'useCart', label: 'proceedToCheckout' },
            })

            // FIXME: this should be refactored as part of FIGS-5648
            // throwing error to maintain expected behavior in `MiniCartContent`
            throw error
          }
        } else {
          // if `session` is missing/empty, report an error and default to standard checkout flow
          reportClientError({
            error: new Error('Loop flow detected but no session found at checkout.'),
            context: { scope: 'useCart', label: 'proceedToCheckout' },
          })
        }
      }

      const checkoutQueryParams: Record<string, string> = {}
      const finalDiscountCode = providedDiscountCode ?? discountCookieVal
      if (finalDiscountCode) checkoutQueryParams['discount'] = finalDiscountCode
      if (facebookCookieVal) checkoutQueryParams['FBCLID'] = facebookCookieVal

      try {
        const redirectUri = await retrieveCheckoutUrl(checkoutQueryParams)

        if (!redirectUri) {
          reportClientError({
            error: new Error('No checkout/redirect URI found.'),
            context: { scope: 'useCart', label: 'proceedToCheckout' },
          })
          return
        }

        reportClientEvent({
          label: 'navigated-to-checkout',
          context: {
            scope: 'useCart',
            itemCount: `${cart.itemCount}`,
            cartValue: `${cart.totalPrice}`,
          },
        })

        trackProceedToCheckout({
          cart,
          currencyCode: currency,
          shopifyCartId: cartStoreCart.id,
          shopifyCheckoutURL: redirectUri,
        })

        window.location.assign(redirectUri)
      } catch (error) {
        setErrorMessage(errorMessages.proceedToCheckout)
        reportClientError({
          error: new Error('Error occurred creating/proceeding to checkout.'),
          context: { scope: 'useCart', label: 'proceedToCheckout' },
        })

        // FIXME: this should be refactored as part of FIGS-5648
        // throwing error to maintain expected behavior in `MiniCartContent`
        throw error
      }
    },
    [
      cart,
      convertCartItemIntoSignalData,
      discountCookieVal,
      facebookCookieVal,
      retrieveLoopCartToken,
      trackProceedToReturnsPortal,
      errorMessages.proceedToCheckout,
      retrieveCheckoutUrl,
      trackProceedToCheckout,
      currency,
      cartStoreCart.id,
    ]
  )

  const isItemRemovalInProgress = useCallback<UseCartOutput['isItemRemovalInProgress']>(
    key => pendingRemovalKeys.has(key),
    [pendingRemovalKeys]
  )

  return {
    cart: displayedCart,
    pendingLines,
    status,
    lastAddedItems,
    errorMessage,
    addGlobalAttributes,
    addItems,
    removeItems,
    updateItemsQuantity,
    addEmbroideryToExistingCartItem,
    proceedToCheckout,
    trackCartOpened,
    isItemRemovalInProgress,
    bundlePrograms: mappedBundlePrograms,
  }
}

export const CartContainer = createContainer(useCart)
CartContainer.Provider.displayName = 'CartProviderImpl'
